import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createBaseSelector, registerSlice } from '@shared/lib/redux'

import { PainterModel } from '../domain/painter.model'
import {
  computeAreaRadiusLayer,
  computeCircleUserCountLayer,
  computeFoundUsersInAreaIconLayer,
  computeMeasureIconLayer
} from '../lib'
import { MIN_PAINTER_SIZE } from '../config'
import { PainterChangeDTO, PainterSetDTO } from '../painter.types'

export type PainterStore = {
  painter: PainterModel | null
  isPaintStarted: boolean
}

const initialState: PainterStore = {
  painter: null,
  isPaintStarted: false
}

export const painterSlice = createSlice({
  name: 'painterSlice',
  initialState,
  reducers: {
    setPainter: (state, action: PayloadAction<PainterSetDTO>) => {
      state.painter = new PainterModel(
        [action.payload.point.lat, action.payload.point.long],
        MIN_PAINTER_SIZE
      )
      state.isPaintStarted = true
    },
    setPainterSize: (state, action: PayloadAction<PainterChangeDTO>) => {
      // TODO: Fix it
      if (!state.painter) {
        state.painter = new PainterModel([0, 0], 0)
        return
      }
      state.painter = {
        ...state.painter,
        radius: action.payload.radius
      }
    },
    setPaintMode: (state, action: PayloadAction<boolean>) => {
      state.isPaintStarted = action.payload
    },
    resetPainter: (state) => {
      state.painter = null
      state.isPaintStarted = false
    }
  }
})

const painterBaseSelector = createBaseSelector(painterSlice)
registerSlice([painterSlice])

const selectPainter = createSelector(painterBaseSelector, (s) => {
  return s.painter
})

const selectPainterLayers = createSelector(
  painterBaseSelector,
  (_: unknown, usersCount: number, currentZoom: number) => ({
    usersCount,
    currentZoom
  }),
  (s, { usersCount, currentZoom }) => {
    const painter = s.painter
    if (!painter) {
      return {
        measurerIconLayer: null,
        radiusTextLayer: null,
        usersCountTextLayer: null,
        usersCountIconLayer: null
      }
    }
    return {
      measurerIconLayer: computeMeasureIconLayer(painter),
      radiusTextLayer: computeAreaRadiusLayer(painter, currentZoom),
      usersCountTextLayer: computeCircleUserCountLayer(
        painter,
        usersCount,
        currentZoom
      ),
      usersCountIconLayer: computeFoundUsersInAreaIconLayer(
        painter,
        usersCount,
        currentZoom
      )
    }
  }
)
const selectIsPaintStarted = createSelector(
  painterBaseSelector,
  (s) => s.isPaintStarted
)

export const painterStore = {
  actions: { ...painterSlice.actions },
  selectors: {
    selectPainter,
    selectPainterLayers,
    selectIsPaintStarted
  },
  name: painterSlice.name
}
